import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  AssignmentIndOutlined,
  AutoAwesomeOutlined,
  NotesOutlined,
  SnippetFolderOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  Container,
  Divider,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Icon,
  SectionHeader,
  SectionNavigation,
  SectionNavigationItem,
  useDialogService,
  Typography,
} from "@platform/design-system";
import { createMailtoLink, MailtoParams } from "@platform/fe-utils";
import { useRfpCarriers, useRouteMatch } from "@platform/hooks";
import { useSignedInUser } from "@platform/user/client";
import { lazy, Suspense, useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router";
import {
  PROPELX,
  RECORD_INSPECTOR,
  BTX_ANALYST_NOTEPAD,
  RFP_ASSISTANT_UI,
  CENSUS_VALIDATION_PAGE,
  RFP_CORRESPONDENCE,
  RUMI_LAUNCHPAD,
} from "@platform/feature-flags";

import { useRfpCoverages } from "../../../api/rfp";
import { EditRfpCarriersDrawer } from "../../../components/editRfpCarriers/drawer";
import { ROUTE_NAMES } from "../../../constants";
import useToastNotificationService from "../../../hooks/useToastNotificationService";
import { toCarriersWithContactsByType } from "../../../utils/carrier";
import { CorrespondenceDrawerContainer } from "../components/Correspondence";
import { useUnsoldCoveragesQuery } from "../components/FinalDecisions/useFinalDecisionApi";
import { RFPDetailsState } from "../types";

import { EmailCarrier, toEmailCarriers } from "./emails/emailCarriers";
import {
  CarrierEmailOptionKey,
  EmailMenuButton,
  EmailMenuOptionKey,
} from "./emails/EmailMenuButton";
import {
  CARRIER_EMAIL_TEMPLATES,
  EMAIL_TEMPLATES,
} from "./emails/emailTemplates";
import { HeaderExtra } from "./HeaderExtra";

// Loaded on demand, gated on enableRfpAssistant below, so react-markdown and
// its remark plugins (the drawer's markdown renderer) never reach the main
// bundle for the majority of users who don't have this flag on.
const LazyRfpAssistantDrawer = lazy(() =>
  import("@platform/rfp-assistant").then((mod) => ({
    default: mod.RfpAssistantDrawer,
  })),
);

// Column widths matching BenefitsClassGrid
const CHECKBOX_COLUMN_WIDTH = 50;
const CLASS_NAME_COLUMN_WIDTH = 200;
const COVERAGE_COLUMN_WIDTH = 150;
const DRAWER_PADDING = 80; // Extra padding for comfortable spacing
const MAX_COVERAGES_BEFORE_FULL_WIDTH = 6;
const SCROLLBAR_BUFFER = 60;
const MIN_DRAWER_WIDTH = 500;

type RfpSubmitter = NonNullable<RFPDetailsState["data"]>["rfpSubmitter"];

/** Mirrors the name shown in RFP Information, where either pair may be set. */
export const getRfpSubmitterName = (
  rfpSubmitter: RfpSubmitter,
): string | undefined => {
  if (rfpSubmitter?.givenName && rfpSubmitter.surname) {
    return `${rfpSubmitter.givenName} ${rfpSubmitter.surname}`;
  }

  if (rfpSubmitter?.firstName && rfpSubmitter.lastName) {
    return `${rfpSubmitter.firstName} ${rfpSubmitter.lastName}`;
  }

  return undefined;
};

/** Calculate drawer width based on number of coverage columns */
export const calculateDrawerWidth = (coverageCount: number): number => {
  // If more than 6 coverages, use full screen width
  if (coverageCount > MAX_COVERAGES_BEFORE_FULL_WIDTH) {
    return window.innerWidth;
  }

  const coverageColumnsWidth = coverageCount * COVERAGE_COLUMN_WIDTH;
  const totalWidth =
    CHECKBOX_COLUMN_WIDTH +
    CLASS_NAME_COLUMN_WIDTH +
    coverageColumnsWidth +
    DRAWER_PADDING;
  // Add buffer for scrollbar and ensure minimum width
  return Math.max(totalWidth + SCROLLBAR_BUFFER, MIN_DRAWER_WIDTH);
};

type RouteKey =
  | "launchpad"
  | "details"
  | "build-2"
  | "census"
  | "track"
  | "present"
  | "deliverables"
  | "final";

const ROUTES: Record<string, RouteKey> = {
  LAUNCHPAD: "launchpad",
  DETAILS: "details",
  BUILD_2: "build-2",
  CENSUS: "census",
  TRACK: "track",
  PRESENT: "present",
  DELIVERABLES: "deliverables",
  FINAL: "final",
};

const BASE_NAV_ITEMS: SectionNavigationItem<RouteKey>[] = [
  { label: "Details", path: "", value: ROUTES.DETAILS },
  {
    label: "Census",
    path: ROUTE_NAMES.RFP_WORKBENCH_CENSUS,
    value: ROUTES.CENSUS,
  },
  {
    label: "Build",
    path: ROUTE_NAMES.RFP_WORKBENCH_BUILD_2,
    value: ROUTES.BUILD_2,
  },
  {
    label: "Track",
    path: ROUTE_NAMES.RFP_WORKBENCH_TRACK,
    value: ROUTES.TRACK,
  },
  {
    label: "Deliverables",
    path: ROUTE_NAMES.RFP_WORKBENCH_DELIVERABLES,
    value: ROUTES.DELIVERABLES,
  },
  {
    label: "Final",
    path: ROUTE_NAMES.RFP_WORKBENCH_FINAL,
    value: ROUTES.FINAL,
  },
];

// Rumi's account of the run comes before the RFP's own tabs: it is what the
// analyst opens the RFP to read once a run has happened. It takes the RFP's
// index route, so Details moves to a path of its own.
const LAUNCHPAD_NAV_ITEM: SectionNavigationItem<RouteKey> = {
  label: "Rumi Launchpad",
  path: ROUTE_NAMES.RFP_WORKBENCH_LAUNCHPAD,
  value: ROUTES.LAUNCHPAD,
};

const PRESENT_NAV_ITEM: SectionNavigationItem<RouteKey> = {
  label: "Present",
  path: ROUTE_NAMES.RFP_WORKBENCH_PRESENT,
  value: ROUTES.PRESENT,
};

export const getNavItems = ({
  enablePropelx,
  enableRumiLaunchpad,
}: {
  enablePropelx: boolean;
  enableRumiLaunchpad: boolean;
}): SectionNavigationItem<RouteKey>[] => {
  let items = [...BASE_NAV_ITEMS];

  if (enableRumiLaunchpad) {
    items = [
      LAUNCHPAD_NAV_ITEM,
      ...items.map((item) =>
        item.value === ROUTES.DETAILS
          ? { ...item, path: ROUTE_NAMES.RFP_WORKBENCH_DETAILS }
          : item,
      ),
    ];
  }

  if (enablePropelx) {
    // Insert Present tab after Track
    const trackIndex = items.findIndex((item) => item.value === ROUTES.TRACK);
    items = [
      ...items.slice(0, trackIndex + 1),
      PRESENT_NAV_ITEM,
      ...items.slice(trackIndex + 1),
    ];
  }

  return items;
};

const ROUTE_PATHS = BASE_NAV_ITEMS.map<[RouteKey, string]>((item) => [
  item.value,
  item.path,
]);

// Add build-2 and present routes to match nested tabs
const ROUTE_PATHS_WITH_BUILD2: [RouteKey, string][] = [
  ...ROUTE_PATHS,
  ["launchpad", ROUTE_NAMES.RFP_WORKBENCH_LAUNCHPAD],
  ["details", ROUTE_NAMES.RFP_WORKBENCH_DETAILS],
  ["present", ROUTE_NAMES.RFP_WORKBENCH_PRESENT],
  ["present", `${ROUTE_NAMES.RFP_WORKBENCH_PRESENT}/:subTab`],
  ["build-2", ROUTE_NAMES.RFP_WORKBENCH_BUILD_2],
  [
    "build-2",
    `${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS}/:planName`,
  ],
];

interface Props {
  status?: string;
  dueDate: string;
  clientName: string;
  brokerName: string;
  effectiveDate: string;
  ecoRfpId: string;
  brokerExternalId: string;
  analystDocsFolderUrl?: string;
  isStatusChanging: boolean;
  hasFinalDecisions: boolean;
  onStatusChange: (params: { status: string }) => void;
  refetchRpfDetails: VoidFunction;
  state: RFPDetailsState;
}

const TITLE_BY_ROUTE: Partial<Record<RouteKey, string>> = {
  details: "RFP Summary",
  "build-2": "Coverages",
  census: "Census",
  track: "Carrier Tracker",
  deliverables: "Broker Delivery Documents",
  final: "Final Decisions",
};

function NewAnalystRfpDetails({
  status,
  dueDate,
  clientName,
  brokerName,
  effectiveDate,
  ecoRfpId,
  brokerExternalId,
  analystDocsFolderUrl,
  state,
  isStatusChanging,
  hasFinalDecisions,
  refetchRpfDetails,
  onStatusChange,
}: Props): React.JSX.Element {
  const enableRfpAssistant = useFeatureIsOn(RFP_ASSISTANT_UI);
  const enablePropelx = useFeatureIsOn(PROPELX);
  const enableRumiLaunchpad = useFeatureIsOn(RUMI_LAUNCHPAD);
  const enableCorrespondence = useFeatureIsOn(RFP_CORRESPONDENCE);
  const enableRecordInspector = useFeatureIsOn(RECORD_INSPECTOR);
  const enableCensusValidation = useFeatureIsOn(CENSUS_VALIDATION_PAGE);
  const enableAnalystNotepad = useFeatureIsOn(BTX_ANALYST_NOTEPAD);
  const notesLabel = enableAnalystNotepad ? "Notes" : "RFP Notes";
  const [isRfpAssistantOpen, setIsRfpAssistantOpen] = useState(false);
  const [isCorrespondenceOpen, setIsCorrespondenceOpen] = useState(false);
  const [isCarriersDrawerOpen, setIsCarriersDrawerOpen] = useState(false);
  const navItems = getNavItems({ enablePropelx, enableRumiLaunchpad });
  const { openDialog } = useDialogService();
  const { createToastNotification } = useToastNotificationService();
  const { id: rfpId } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const { isAdmin } = useSignedInUser();

  const clientId = state.data?.clientId;
  const clientNameTitle = clientId ? (
    <Link
      to={`/${ROUTE_NAMES.CLIENTS}/${clientId}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit" }}
    >
      {clientName}
    </Link>
  ) : (
    clientName
  );

  // Pre-fetch coverages to calculate drawer width before opening
  const { data: rfpCoverages = [] } = useRfpCoverages(ecoRfpId, {
    enabled: !!ecoRfpId,
  });
  const { data: unsoldCoverages } = useUnsoldCoveragesQuery({
    rfpId: rfpId!,
  });
  const { data: rfpCarriers, refetch: refetchCarriers } = useRfpCarriers({
    rfpId: rfpId!,
  });
  const emailCarriers = rfpCarriers ? toEmailCarriers(rfpCarriers) : [];
  const carriersWithContacts = rfpCarriers
    ? toCarriersWithContactsByType(rfpCarriers)
    : [];

  const route = useRouteMatch(ROUTE_PATHS_WITH_BUILD2, {
    baseRoute: `/${ROUTE_NAMES.RFPS}/:rfpId/`,
  });

  const isBuild2Forms = pathname.includes(
    `${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2_FORMS}`,
  );
  const backgroundColor = isBuild2Forms ? "background.default" : "inherit";

  const handleOpenDrawer = (): void => {
    const drawerWidth = calculateDrawerWidth(rfpCoverages.length);
    openDialog({
      type: "benefitsClassManager",
      data: {
        ecoRfpId,
        clientName,
        rfpId,
      },
      width: drawerWidth,
    });
  };

  const handleOpenInspector = (): void => {
    const numericRfpId = rfpId ? Number(rfpId) : NaN;

    if (!Number.isInteger(numericRfpId) || numericRfpId <= 0) {
      return;
    }

    openDialog({
      type: "recordInspector",
      data: { type: "rfp", id: numericRfpId },
      width: 800,
    });
  };

  const handleOpenNoteDrawer = (): void => {
    openDialog({
      type: "rfpNotes",
      data: {
        ecoRfpId,
        brokerExternalId,
        clientName,
      },
      width: 600,
    });
  };

  const openMailDraft = (emailPayload: MailtoParams): void => {
    window.open(
      createMailtoLink(emailPayload),
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleSelectCarrierEmail = (
    optionKey: CarrierEmailOptionKey,
    carrier: EmailCarrier,
  ): void => {
    openMailDraft(
      CARRIER_EMAIL_TEMPLATES[optionKey]({
        carrierName: carrier.name,
        salesRepEmails: carrier.salesRepEmails,
        clientExecutiveEmail: null, // TODO: Replace once we have the value in the system
        analystName: state.data?.rfpAnalyst?.name,
        groupName: clientName,
        carrierDueDate: state.data?.carrierDueDate,
        today: new Date(),
      }),
    );
  };

  const handleSelectEmailOption = (optionKey: EmailMenuOptionKey): void => {
    const buildEmail = EMAIL_TEMPLATES[optionKey];

    if (!buildEmail) {
      createToastNotification({
        severity: "info",
        message: "This email is not available yet.",
      });
      return;
    }

    const rfpSubmitter = state.data?.rfpSubmitter;
    if (!rfpSubmitter?.email) {
      createToastNotification({
        severity: "error",
        message:
          "RFP submitter details are missing. Please contact support to resolve this issue.",
      });
      return;
    }

    openMailDraft(
      buildEmail({
        rfpSubmitterEmail: rfpSubmitter.email,
        rfpSubmitterName: getRfpSubmitterName(rfpSubmitter)!,
        clientExecutiveEmail: null, // TODO: Replace once we have the value in the system
        analystName: state.data?.rfpAnalyst?.name,
        groupName: clientName,
        effectiveDate,
        linesOfCoverage: unsoldCoverages?.map(({ product }) => product.name),
      }),
    );
  };

  // The census validation page draws its own heading and container, so the
  // tab shell renders it bare. Without the flag the census tab is the older
  // tool, which still expects both.
  const bareRoutes: RouteKey[] = enableCensusValidation
    ? [ROUTES.FINAL, ROUTES.BUILD_2, ROUTES.CENSUS]
    : [ROUTES.FINAL, ROUTES.BUILD_2];

  const context = {
    refetchRpfDetails,
    state,
    clientName,
    ecoRfpId,
    brokerExternalId,
  };

  return (
    <Box mt={8} flex={1} sx={{ backgroundColor }}>
      <SectionHeader
        title={clientNameTitle}
        subtitle={`Broker: ${brokerName}`}
        extra={
          <HeaderExtra
            status={status}
            effectiveDate={effectiveDate}
            dueDate={dueDate}
            isStatusChanging={isStatusChanging}
            hasFinalDecisions={hasFinalDecisions}
            onStatusChange={onStatusChange}
          />
        }
      />
      <Box sx={{ backgroundColor: "background.paper" }}>
        <SectionNavigation
          value={route}
          navItems={navItems}
          slotProps={{
            container: {
              sx: {
                px: 3,
                display: "flex",
                overflowX: "auto",
                alignItems: "center",
                columnGap: 2,
              },
            },
            tabs: {
              textColor: "secondary",
              indicatorColor: "secondary",
              sx: {
                "& .MuiTabs-indicator": {
                  backgroundColor: "brandPrimary.main",
                },
                "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
              },
            },
          }}
          extra={
            <>
              {isAdmin && enableRecordInspector && (
                <Tooltip title="Inspect record">
                  <IconButton
                    aria-label="Inspect record"
                    onClick={handleOpenInspector}
                  >
                    <VisibilityOutlined />
                  </IconButton>
                </Tooltip>
              )}
              {enableRfpAssistant && (
                <Tooltip title="RFP Assistant">
                  <IconButton
                    aria-label="Open RFP Assistant"
                    onClick={() => setIsRfpAssistantOpen(true)}
                  >
                    <AutoAwesomeOutlined color="brandPrimary" />
                  </IconButton>
                </Tooltip>
              )}
              {enableCorrespondence && (
                <Tooltip title="Communications">
                  <IconButton
                    aria-label="Open communications"
                    onClick={() => setIsCorrespondenceOpen(true)}
                  >
                    <Icon name="mail" color="secondary" />
                  </IconButton>
                </Tooltip>
              )}
              <EmailMenuButton
                carriers={emailCarriers}
                onSelect={handleSelectEmailOption}
                onSelectCarrierEmail={handleSelectCarrierEmail}
                onAddMissingSalesRepEmail={() => setIsCarriersDrawerOpen(true)}
              />
              <Tooltip title={notesLabel}>
                <IconButton
                  aria-label={`Open ${notesLabel}`}
                  onClick={handleOpenNoteDrawer}
                >
                  <NotesOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Class Manager">
                <IconButton
                  aria-label="Open class Manager"
                  onClick={handleOpenDrawer}
                >
                  <AssignmentIndOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Documents">
                <span>
                  <IconButton
                    aria-label="Open Documents"
                    disabled={!analystDocsFolderUrl}
                    onClick={() =>
                      window.open(
                        analystDocsFolderUrl,
                        "_blank",
                        "noopener noreferrer",
                      )
                    }
                  >
                    <SnippetFolderOutlined color="brandPrimary" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          }
        />
      </Box>
      <Divider />
      {route === ROUTES.PRESENT ? (
        <Outlet context={context} />
      ) : (
        <Container sx={{ pt: isBuild2Forms ? 0 : 5, mb: 2, height: "100%" }}>
          {bareRoutes.includes(route) ? (
            <Outlet context={context} />
          ) : (
            <>
              {TITLE_BY_ROUTE[route] && (
                <Typography variant="display-small" sx={{ fontWeight: 400 }}>
                  {TITLE_BY_ROUTE[route]}
                </Typography>
              )}
              <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                <Outlet context={context} />
              </Paper>
            </>
          )}
        </Container>
      )}
      {isCarriersDrawerOpen && (
        <EditRfpCarriersDrawer
          rfpId={rfpId!}
          carriers={carriersWithContacts}
          onClose={() => setIsCarriersDrawerOpen(false)}
          onSuccess={() => refetchCarriers()}
        />
      )}
      {enableCorrespondence && (
        <CorrespondenceDrawerContainer
          open={isCorrespondenceOpen}
          onClose={() => setIsCorrespondenceOpen(false)}
          rfpCanonicalIdentifier={ecoRfpId}
          subtitle={`${clientName} · ${brokerName}`}
        />
      )}
      {enableRfpAssistant && (
        <Suspense fallback={null}>
          <LazyRfpAssistantDrawer
            open={isRfpAssistantOpen}
            onClose={() => setIsRfpAssistantOpen(false)}
            rfpId={ecoRfpId}
            rfpLabel={`${clientName} — RFP #${rfpId}`}
          />
        </Suspense>
      )}
    </Box>
  );
}

export { NewAnalystRfpDetails };
